import React, { useState } from "react";
import PropTypes from "prop-types";
import { Formik, Form, Field } from "formik";
import { generateValidation } from "../ContactUsForm/validations";
import ReCaptcha from "../ContactUsForm/ReCaptcha";
import { hygieneCampaignFormServerAPI } from "@/services/formSubmitAPI/client";
import { capitalizeFirstLetter } from "@/utils/formatters";
import { useContentfulTranslations } from "@/utils/contentfulTranslations";
import { useLocale } from "@/hooks/useLocale";
import { openPopup } from "@/utils/helper";

const captchaDisabled = process.env.NEXT_PUBLIC_RECAPTCHA_DISABLE === "0";
const optionalFieldNames = new Set(["first_name", "last_name", "phone"]);

// Returns red border class if field has error, normal border otherwise
function inputClass(name, errors, touched) {
  const base = "w-full border rounded px-3 py-2 text-[15px]";
  return errors[name] && touched[name]
    ? `${base} border-red-500`
    : `${base} border-slate-300`;
}

// Returns aria-describedby id only when field has a visible error
function ariaDescribedBy(name, errors, touched) {
  return errors[name] && touched[name] ? `${name}-error` : undefined;
}

const HygieneCampaign = ({
  formConfig = {},
  hygieneCampaignFormConfigurationsData = {},
}) => {
  const locale = useLocale();
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const contactFormConfigurations =
    hygieneCampaignFormConfigurationsData?.items?.[0]?.fields?.configurations.filter(
      config =>
        config?.fields?.internalTitle ===
        `${capitalizeFirstLetter(process.env.NEXT_PUBLIC_COUNTRY_NAME)} - Contact Us Form`
    ) || [];
  const filteredContactFormConfigurations =
    contactFormConfigurations?.[0]?.fields?.config;

  const { successText } = useContentfulTranslations();
  const {
    formFields = [],
    heading = "",
    url = "",
    privacyPolicy = "",
    submit = "Submit",
    submitting = "Submitting",
    selectList = "Select",
    capchaErrorMessage = "Please verify the captcha",
    capchaVerificationMessage = "Captcha verification failed",
    tryAgainMessage = "Something went wrong. Please try again.",
  } = formConfig;

  const captchaField = {
    name: "captchaToken",
    type: "captcha",
    required: true,
    message: capchaErrorMessage,
  };

  const normalizedFormFields = React.useMemo(
    () =>
      formFields.map(field =>
        optionalFieldNames.has(field.name)
          ? { ...field, required: false }
          : field
      ),
    [formFields]
  );

  // One empty string per field as starting value
  const initialValues = React.useMemo(
    () =>
      normalizedFormFields.reduce(
        (acc, field) => ({ ...acc, [field.name]: "" }),
        {
          captchaToken: "",
        }
      ),
    [normalizedFormFields]
  );

  // Captcha validation only needed in production
  const validationSchema = generateValidation([
    ...normalizedFormFields,
    ...(captchaDisabled ? [] : [captchaField]),
  ]);

  async function handleSubmit(
    values,
    { setSubmitting, setFieldError, resetForm }
  ) {
    setSubmitError(null);

    try {
      if (captchaDisabled) {
        const res = await hygieneCampaignFormServerAPI(values.captchaToken, {
          values: payload,
          filteredContactFormConfigurations,
        });
        if (res?.message) {
          resetForm();
          setSuccess(true);
        }
        setSubmitting(false);
        return;
      }

      // Step 1: Check captcha token exists
      if (!values.captchaToken) {
        setFieldError("captchaToken", capchaErrorMessage);
        setSubmitting(false);
        return;
      }
      // Step 2: Verify captcha with server
      const fieldNames = new Set(normalizedFormFields.map(f => f.name));

      const payload = Object.fromEntries(
        Object.entries(values).filter(([key]) => fieldNames.has(key))
      );
      const res = await hygieneCampaignFormServerAPI(values.captchaToken, {
        values: payload,
        filteredContactFormConfigurations,
      });
      if (res?.message) {
        resetForm();
        setSuccess(true);
      } else {
        setFieldError("captchaToken", capchaVerificationMessage);
        setSubmitting(false);
        return;
      }
    } catch (err) {
      console.error(err);
      setSubmitError(tryAgainMessage);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-[980px] px-4 py-[50px]">
      <h2 className="text-center text-[45px] md:text-[60px] font-[Helvetica-Light,_Helvetica,_sans-serif] text-[#232323]">
        {heading}
      </h2>
      <hr className="mt-3 mb-8 border-t border-black-300" />
      {success ? (
        <div className="flex justify-center items-center">
          <div className="w-full max-w-md text-center">
            <h3 className="text-lg font-semibold !text-[21px] text-gray-800">
              <span
                className="font-[HelveticaRoman,_Helvetica,_sans-serif]"
                dangerouslySetInnerHTML={{ __html: successText?.[locale] }}
              />
            </h3>
          </div>
        </div>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, setFieldValue, submitCount, isSubmitting }) => (
            <Form className="space-y-5 w-3/4 mx-auto " noValidate>
              {/* ── Fields ── */}
              {normalizedFormFields.map(field => (
                <div
                  key={field.name}
                  className="grid gap-2 md:gap-4 items-start md:grid-cols-[220px_minmax(0,328px)]"
                >
                  <label
                    htmlFor={field.name}
                    className="text-sm text-slate-700 md:text-right md:pt-2"
                  >
                    {field.label}
                    {field.required && (
                      <span aria-hidden="true" className="ml-1 text-red-500">
                        *
                      </span>
                    )}
                  </label>

                  <div className="max-w-[328px] w-full">
                    {field.type === "select" && (
                      <Field
                        as="select"
                        id={field.name}
                        name={field.name}
                        className={inputClass(field.name, errors, touched)}
                        aria-invalid={Boolean(
                          errors[field.name] && touched[field.name]
                        )}
                        aria-describedby={ariaDescribedBy(
                          field.name,
                          errors,
                          touched
                        )}
                      >
                        <option value="">-- {selectList} --</option>
                        {field.options?.map(opt => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </Field>
                    )}

                    {(field.type === "text" || field.type === "email") && (
                      <Field
                        type={field.type}
                        id={field.name}
                        name={field.name}
                        className={inputClass(field.name, errors, touched)}
                        aria-invalid={Boolean(
                          errors[field.name] && touched[field.name]
                        )}
                        aria-describedby={ariaDescribedBy(
                          field.name,
                          errors,
                          touched
                        )}
                        onInput={
                          field.name === "first_name" ||
                          field.name === "last_name"
                            ? e => {
                                const value = e.target.value.replace(
                                  /[^a-zA-Z]/g,
                                  ""
                                );
                                e.target.value = value;
                              }
                            : field.name === "phone"
                              ? e => {
                                  const value = e.target.value.replace(
                                    /\D/g,
                                    ""
                                  );
                                  e.target.value = value;
                                }
                              : undefined
                        }
                      />
                    )}

                    {field.type === "textarea" && (
                      <Field
                        as="textarea"
                        id={field.name}
                        name={field.name}
                        rows="5"
                        className={inputClass(field.name, errors, touched)}
                        aria-invalid={Boolean(
                          errors[field.name] && touched[field.name]
                        )}
                        aria-describedby={ariaDescribedBy(
                          field.name,
                          errors,
                          touched
                        )}
                      />
                    )}

                    {errors[field.name] && touched[field.name] && (
                      <p
                        id={`${field.name}-error`}
                        role="alert"
                        className="text-red-600 text-xs mt-1"
                      >
                        {errors[field.name]}
                      </p>
                    )}
                  </div>
                </div>
              ))}

              {/* ── reCAPTCHA ── */}
              {!captchaDisabled && (
                <div className="grid md:grid-cols-[220px_minmax(0,328px)] md:gap-4">
                  <div />
                  <div className="max-w-[328px] w-full">
                    <div
                      className={
                        errors.captchaToken && submitCount > 0
                          ? "[&_iframe]:border-2 [&_iframe]:border-red-500 [&_iframe]:rounded"
                          : ""
                      }
                    >
                      <ReCaptcha
                        siteKey={process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY}
                        onChange={token => {
                          setFieldValue("captchaToken", token || "");
                        }}
                        onExpired={() => setFieldValue("captchaToken", "")}
                        onError={() => setFieldValue("captchaToken", "")}
                      />
                    </div>

                    {errors.captchaToken && submitCount > 0 && (
                      <p
                        id="captchaToken-error"
                        role="alert"
                        className="text-red-600 text-xs mt-1"
                      >
                        {errors.captchaToken}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* ── Submit error ── */}
              {submitError && (
                <div
                  role="alert"
                  aria-live="assertive"
                  className="text-red-600 text-sm text-center"
                >
                  {submitError}
                </div>
              )}

              {/* ── Submit button ── */}
              <div className="grid md:grid-cols-[220px_minmax(0,328px)] md:gap-4 mt-2">
                <div />
                <div className="max-w-[328px] w-full">
                  {url && privacyPolicy && (
                    <a
                      href={url}
                      rel="noopener noreferrer"
                      className="block mb-3 text-sm text-blue-600 underline"
                      onClick={e => openPopup(e, url)}
                    >
                      {privacyPolicy}
                    </a>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="font-[HelveticaBold,Helvetica,_sans-serif] w-full bg-[#3E4A75] text-white py-3 uppercase rounded disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? submitting : submit}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};

// ── PropTypes ─────────────────────────────────────────────────────────────────

const FieldShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  type: PropTypes.oneOf(["text", "email", "textarea", "select"]).isRequired,
  required: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.string),
});

HygieneCampaign.propTypes = {
  formConfig: PropTypes.shape({
    heading: PropTypes.string,
    url: PropTypes.string,
    privacyPolicy: PropTypes.string,
    formFields: PropTypes.arrayOf(FieldShape),
  }),
  hygieneCampaignFormConfigurationsData: PropTypes.object,
};

export default HygieneCampaign;
